import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, AlertTriangle, ListChecks, Loader2 } from "lucide-react";

const STATUS_META = {
  done: {
    icon: CheckCircle2,
    iconClass: "text-emerald-600",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    badgeLabel: "Erledigt",
  },
  open: {
    icon: Circle,
    iconClass: "text-slate-400",
    badgeClass: "bg-slate-50 text-slate-600 border-slate-200",
    badgeLabel: "Offen",
  },
  warning: {
    icon: AlertTriangle,
    iconClass: "text-amber-600",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    badgeLabel: "Prüfen",
  },
};

export function YearEndChecklist({ companyId, year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const y = parseInt(year, 10);
    if (!companyId || !y || y < 1900 || y > 3000) return;
    setLoading(true);
    setError("");
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/year-end-checklist`,
        { params: { year: y } },
      );
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Checklist konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [companyId, year]);

  const rcPdf = async () => {
    try {
      const r = await apiClient.get(`/companies/${companyId}/vat/reverse-charge-report.pdf?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `Bezugsteuer-Meldung-ESTV-${year}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("PDF konnte nicht erstellt werden"); }
  };
  const rcSubmitted = async (submitted) => {
    try {
      await apiClient.put(`/companies/${companyId}/vat/reverse-charge-report/submitted?year=${year}`, { submitted });
      toast.success(submitted ? "Bezugsteuer-Meldung als eingereicht markiert – PDF im Dokumentenarchiv «Steuern»" : "Markierung aufgehoben");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Konnte nicht speichern"); }
  };

  useEffect(() => {
    load();
  }, [load]);

  const items = data?.items || [];
  const doneCount = items.filter((i) => i.status === "done").length;
  const openCount = items.filter((i) => i.status === "open").length;
  const warnCount = items.filter((i) => i.status === "warning").length;

  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="year-end-checklist-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-slate-50 flex items-center justify-center flex-shrink-0">
            <ListChecks className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Abschluss-Checkliste {year}
            </h2>
            <p className="text-xs text-slate-500">
              Geführte Schritte für den Jahresabschluss nach OR. Status wird live aus
              den Buchhaltungsdaten ermittelt.
            </p>
          </div>
        </div>
        {data && (
          <div className="flex items-center gap-2 text-xs">
            <Badge
              variant="outline"
              className="bg-emerald-50 text-emerald-700 border-emerald-200"
              data-testid="year-end-checklist-done-count"
            >
              {doneCount} erledigt
            </Badge>
            {warnCount > 0 && (
              <Badge
                variant="outline"
                className="bg-amber-50 text-amber-700 border-amber-200"
                data-testid="year-end-checklist-warn-count"
              >
                {warnCount} prüfen
              </Badge>
            )}
            {openCount > 0 && (
              <Badge
                variant="outline"
                className="bg-slate-50 text-slate-600 border-slate-200"
                data-testid="year-end-checklist-open-count"
              >
                {openCount} offen
              </Badge>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="year-end-checklist-loading">
          <Loader2 className="h-3 w-3 animate-spin" /> Lade Checklist …
        </div>
      )}
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      {data && !loading && (
        <ol className="space-y-2" data-testid="year-end-checklist-list">
          {items.map((item, idx) => {
            const meta = STATUS_META[item.status] || STATUS_META.open;
            const Icon = meta.icon;
            return (
              <li
                key={item.key}
                className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:bg-slate-50 transition-colors"
                data-testid={`year-end-checklist-item-${item.key}`}
                data-status={item.status}
              >
                <div className="flex items-center justify-center h-5 w-5 mt-0.5 flex-shrink-0">
                  <Icon className={`h-5 w-5 ${meta.iconClass}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-400">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm font-medium text-slate-900">
                      {item.label}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] h-5 ${meta.badgeClass}`}
                    >
                      {meta.badgeLabel}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {item.description}
                  </div>
                  {item.detail && (
                    <div className="text-[11px] text-slate-600 mt-1 font-mono">
                      → {item.detail}
                    </div>
                  )}
                  {item.key === "reverse_charge_report" && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="year-end-rc-actions">
                      <button type="button" onClick={rcPdf} className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100" data-testid="year-end-rc-pdf-btn">ESTV-Meldung {year} (PDF)</button>
                      {item.status !== "done" ? (
                        <button type="button" onClick={() => rcSubmitted(true)} className="rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-700" data-testid="year-end-rc-submit-btn">Als eingereicht markieren (PDF archivieren)</button>
                      ) : (
                        <button type="button" onClick={() => rcSubmitted(false)} className="rounded-md px-2 py-1 text-xs text-slate-600 hover:underline" data-testid="year-end-rc-unsubmit-btn">Markierung aufheben</button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
